import {
  ArrowLeft,
  ArrowUpRight,
  Columns2,
  FileText,
  ListChecks,
  MessageSquare,
  PanelLeft,
  PanelLeftClose,
  SlidersHorizontal,
  ShieldCheck,
  Users,
} from "lucide-react"
import * as React from "react"
import { Link } from "react-router"

import { useNavVisibility } from "@/components/shell"
import { cn } from "@/lib/utils"

import type { DetectorProfile, DetectorProfileResolved } from "@/components/domain/detector-profile"

import type { PresenceViewer } from "./api"
import { MessagesPopover } from "./messages/messages-popover"
import type { Finding, LiveState, Message } from "./data/review-data"
import { ReviewLiveIndicator } from "./people/review-live-indicator"
import { ReviewSettingsPopover } from "./review-settings-popover"

export type ReviewViewMode = "findings" | "side_by_side"

/**
 * Header layout is three visually-quiet clusters:
 *
 *   [ back · title · chips · live ]      … identity
 *                              [ presence · view · actions · report ]
 *
 * The right side is grouped so the eye reads *state* → *view* → *do*.
 * Actions live inside a single segmented rail sharing height, radius,
 * and border so they read as one control surface rather than three
 * floating icons. The report CTA is the terminal cell of that rail —
 * still primary, but visually seated in the group instead of sitting
 * apart in a slab of orange.
 *
 * Messages use a popover-first pattern: a quick glance never leaves the
 * workspace, and only a real working session promotes to the side sheet.
 */

export interface ReviewHeaderProps {
  productName: string
  epaRegNo: string
  version: string
  liveState: LiveState
  presence?: PresenceViewer[]
  view: ReviewViewMode
  onViewChange: (view: ReviewViewMode) => void
  unreadMessageCount: number
  messages: readonly Message[]
  findings: Finding[]
  onOpenPeople: () => void
  onOpenMessages: () => void
  onOpenClientReport: () => void
  backHref: string
  /** The detector settings the run applied. Null until it resolves them. */
  detectorProfile: DetectorProfileResolved | null
  detectorProfileRequested: DetectorProfile
}

export function ReviewHeader({
  productName,
  epaRegNo,
  version,
  liveState,
  presence = [],
  view,
  onViewChange,
  unreadMessageCount,
  messages,
  findings,
  onOpenPeople,
  onOpenMessages,
  onOpenClientReport,
  backHref,
  detectorProfile,
  detectorProfileRequested,
}: ReviewHeaderProps): JSX.Element {
  return (
    <header className="flex h-14 items-center gap-3 border-b border-border bg-surface px-4">
      <Link
        to={backHref}
        aria-label="Back to the review queue"
        className="grid size-8 place-items-center rounded-md text-text-2 transition-colors duration-120 ease-out hover:bg-surface-sunken hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
      </Link>

      {/* One row, not a stacked title and caption: the registration number is
          what identifies the product to a regulator, so it sits beside the name
          at the same weight rather than beneath it as metadata. Chips keep each
          identifier individually selectable and stop the line reading as prose. */}
      <NavToggle />

      {/* A rule, not a gap: leaving the workspace and naming what you are in are
          different kinds of control, and at this density a gap alone reads as
          one run of chrome. */}
      <span aria-hidden="true" className="h-5 w-px shrink-0 bg-border" />

      <div className="flex min-w-0 items-center gap-2.5">
        {/* The subject of the screen, marked once. A regulated label is the thing
            being reviewed, so it carries a mark the way a file does in a file
            manager — recognisable before the name is read. */}
        <ShieldCheck className="size-4 shrink-0 text-brand" aria-hidden="true" />
        <h1 className="truncate text-[15px] font-semibold leading-tight text-text">
          {productName}
        </h1>
        {/* The chip repeats nothing: a review whose product name is unknown falls
            back to its registration number, and showing that number twice reads
            as a defect rather than as two facts. */}
        {productName !== epaRegNo && <IdentifierChip>{epaRegNo}</IdentifierChip>}
        <IdentifierChip>{version}</IdentifierChip>
        <ReviewLiveIndicator state={liveState} />
      </div>

      <div className="ml-auto flex items-center gap-2">
        {presence.length > 0 && <PresencePill viewers={presence} onClick={onOpenPeople} />}
        <ViewSwitcher view={view} onChange={onViewChange} />
        <ActionRail
          onOpenPeople={onOpenPeople}
          onOpenMessages={onOpenMessages}
          unreadMessageCount={unreadMessageCount}
          messages={messages}
          findings={findings}
          detectorProfile={detectorProfile}
          detectorProfileRequested={detectorProfileRequested}
        />
        <ClientReportButton onOpenClientReport={onOpenClientReport} />
      </div>
    </header>
  )
}

/**
 * Hides or restores the application's left navigation.
 *
 * Lives in this header rather than in the navigation itself, because the state
 * it controls is "I am reading a document and want the width": the reviewer is
 * looking here, and once the navigation is gone it cannot carry its own way
 * back.
 */
function NavToggle(): JSX.Element {
  const { hidden, setHidden } = useNavVisibility()
  return (
    <button
      type="button"
      onClick={() => {
        setHidden(!hidden)
      }}
      aria-pressed={hidden}
      aria-label={hidden ? "Show navigation" : "Hide navigation"}
      title={hidden ? "Show navigation" : "Hide navigation"}
      className="hidden size-8 place-items-center rounded-md text-text-3 transition-colors duration-120 ease-out hover:bg-surface-sunken hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)] md:grid"
    >
      {hidden ? (
        <PanelLeft className="size-4" aria-hidden="true" />
      ) : (
        <PanelLeftClose className="size-4" aria-hidden="true" />
      )}
    </button>
  )
}

/**
 * An identifier set in the header's title row.
 *
 * Monospace and tabular so a registration number reads as a code rather than
 * as words, and so two of them line up when stacked in a list elsewhere.
 */
function IdentifierChip({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="shrink-0 rounded-md border border-border-soft bg-surface-sunken px-2 py-0.5 font-id text-caption tabular-nums text-text-2">
      {children}
    </span>
  )
}

/**
 * A segmented rail of the review's secondary actions.
 *
 * Both controls share height and border radius so the group reads as one
 * surface rather than as loose icons. The review's terminal action is not here:
 * a primary action seated in a rail is findable only by reading it, so it
 * stands on its own beside the group.
 */
function ActionRail({
  onOpenPeople,
  onOpenMessages,
  unreadMessageCount,
  messages,
  findings,
  detectorProfile,
  detectorProfileRequested,
}: {
  onOpenPeople: () => void
  onOpenMessages: () => void
  unreadMessageCount: number
  messages: readonly Message[]
  findings: Finding[]
  detectorProfile: DetectorProfileResolved | null
  detectorProfileRequested: DetectorProfile
}): JSX.Element {
  return (
    <div className="inline-flex h-9 items-stretch overflow-hidden rounded-full border border-border bg-surface shadow-e1">
      <RailIconButton
        label="People and routing"
        icon={<Users className="size-4" aria-hidden="true" />}
        onClick={onOpenPeople}
      />
      <RailDivider />
      <MessagesPopover messages={messages} findings={findings} onOpenFullSheet={onOpenMessages}>
        <RailIconButton
          label="Messages"
          icon={<MessageSquare className="size-4" aria-hidden="true" />}
          unreadCount={unreadMessageCount}
        />
      </MessagesPopover>
      <RailDivider />
      <ReviewSettingsPopover resolved={detectorProfile} requested={detectorProfileRequested}>
        <RailIconButton
          label="Settings"
          icon={<SlidersHorizontal className="size-4" aria-hidden="true" />}
        />
      </ReviewSettingsPopover>
    </div>
  )
}

/**
 * The review's terminal action, standing apart from the rail.
 *
 * Inside it, the button shared the rail's border and radius and read as a
 * fourth icon; a primary action has to be findable without being read.
 *
 * Always available, as on the legacy console: the report states an open
 * escalation itself ("Referred to a specialist"), so a finding still with a
 * specialist is something the report shows rather than a reason to hide it.
 */
function ClientReportButton({
  onOpenClientReport,
}: {
  onOpenClientReport: () => void
}): JSX.Element {
  return (
    <div>
      <button
        type="button"
        onClick={onOpenClientReport}
        aria-label="Open client report"
        className={cn(
          "group inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-body-sm font-medium transition-colors duration-120 ease-out",
          "bg-brand text-white shadow-e1 hover:bg-brand-strong",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
          "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled disabled:shadow-none",
        )}
      >
        <FileText className="size-3.5" aria-hidden="true" />
        <span>Client report</span>
        <ArrowUpRight
          className="size-3 opacity-60 transition-opacity duration-120 group-hover:opacity-100 group-disabled:opacity-40"
          aria-hidden="true"
        />
      </button>
    </div>
  )
}

/**
 * A ghost-icon button sized for the action rail. Same height as the rail
 * so the border of the rail acts as the button's border and no per-button
 * chrome accumulates.
 *
 * `unreadCount` is deliberately not a red badge. Red bubbles are alert
 * chrome — they suit an outage but overshoot a social feed. Here an unread
 * message is represented by a small brand-tint dot in the corner, plus
 * the count in the accessible name so screen readers still hear it.
 */
const RailIconButton = React.forwardRef<
  HTMLButtonElement,
  {
    label: string
    icon: React.ReactNode
    unreadCount?: number
    onClick?: () => void
  } & React.ComponentPropsWithoutRef<"button">
>(function RailIconButton({ label, icon, unreadCount, onClick, className, ...props }, ref) {
  const hasUnread = typeof unreadCount === "number" && unreadCount > 0
  const accessibleName = hasUnread ? `${label}, ${unreadCount} unread` : label
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label={accessibleName}
      className={cn(
        "relative grid w-9 place-items-center text-text-2 transition-colors duration-120 ease-out",
        "hover:bg-surface-sunken hover:text-text",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--brand)]",
        className,
      )}
      {...props}
    >
      {icon}
      {hasUnread && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-1 top-1 grid h-3 min-w-3 place-items-center rounded-full bg-brand px-[3px] font-id text-[8px] font-semibold leading-none text-white ring-[1.5px] ring-surface"
        >
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  )
})

function RailDivider(): JSX.Element {
  return <span aria-hidden="true" className="my-1.5 w-px bg-border-soft" />
}

/**
 * Compact indicator showing how many teammates are currently looking at
 * this review. Presence is projected to role only — never to name —
 * because staff identity is confidential when the review is visible to
 * a client channel.
 */
const PRESENCE_TONE: Record<string, string> = {
  Reviewer: "bg-info text-white ring-info/30",
  Specialist: "bg-people text-white ring-people/30",
  Client: "bg-print text-white ring-print/30",
}

function PresencePill({
  viewers,
  onClick,
}: {
  viewers: PresenceViewer[]
  onClick: () => void
}): JSX.Element {
  const label =
    viewers.length === 1
      ? `1 teammate is on this review`
      : `${viewers.length} teammates are on this review`
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label}. Open people and routing.`}
      className="group inline-flex h-9 items-center gap-2 rounded-full border border-border bg-surface py-1 pl-1 pr-2.5 text-caption font-medium text-text-2 shadow-e1 transition-colors duration-120 ease-out hover:border-border-strong hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
    >
      <span className="flex -space-x-1.5" aria-hidden="true">
        {viewers.slice(0, 3).map((v) => {
          // A live-frame viewer with no role slipped through the socket at least
          // once (backend sent `role: null` on the presence frame). Rather than
          // crash the whole workspace, render a neutral badge — the roster
          // display can still tell you *someone* is here.
          const role = v.role ?? ""
          const initial = role.length > 0 ? role[0] : "?"
          return (
            <span
              key={v.userId}
              title={role || "unknown role"}
              className={cn(
                "grid size-5 place-items-center rounded-full font-id text-[10px] font-semibold uppercase leading-none ring-2 ring-surface",
                PRESENCE_TONE[role] ?? "bg-surface-sunken text-text-2 ring-surface",
              )}
            >
              {initial}
            </span>
          )
        })}
        {viewers.length > 3 && (
          <span className="grid size-5 place-items-center rounded-full bg-brand-tint text-[10px] font-semibold text-brand ring-2 ring-surface">
            +{viewers.length - 3}
          </span>
        )}
      </span>
      <span className="tabular-nums text-text">{viewers.length}</span>
    </button>
  )
}

const VIEW_OPTIONS: Array<{
  id: ReviewViewMode
  label: string
  icon: typeof ListChecks
}> = [
  { id: "findings", label: "Findings", icon: ListChecks },
  { id: "side_by_side", label: "Side by side", icon: Columns2 },
]

function ViewSwitcher({
  view,
  onChange,
  className,
}: {
  view: ReviewViewMode
  onChange: (view: ReviewViewMode) => void
  className?: string
}): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Workspace view"
      className={cn(
        "inline-flex h-9 items-center rounded-full border border-border bg-surface-sunken/70 p-0.5 shadow-e1",
        className,
      )}
    >
      {VIEW_OPTIONS.map((opt) => {
        const active = view === opt.id
        return (
          <button
            key={opt.id}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-3 py-1 text-caption font-medium transition-colors duration-120 ease-out",
              active ? "bg-surface text-text shadow-e1" : "text-text-2 hover:text-text",
            )}
          >
            {/* The icon says what shape the view takes — a list against two
                columns — which a reader recognises faster than either label. */}
            <opt.icon className="size-3.5" aria-hidden="true" />
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
